// src/hooks/useCartActions.ts
import { Order, Product } from "@/interfaces/domain/menu";
import { useItemsStore, useOrdersStore, useUIStore } from "../store";
import { formatPrice } from "../utils/formatersPrecio";
import { fetchMenuDtoFresh } from "../services/restaurantDataService";
import { mapDTOToDomain } from "../mappers/menu.mappers";

function computeOrderPrice(order: Order, allProducts: Product[]): number {
  const product = allProducts.find(p => p.id === order.productId);
  if (!product || product.price === null) return order.price;
  let price = product.price;
  for (const [groupId, selections] of Object.entries(order.optionSelection ?? {})) {
    const group = product.options.find(g => g.id === groupId);
    if (!group?.adds_price) continue;
    for (const [itemId, qty] of Object.entries(selections)) {
      const item = group.items.find(i => i.id === itemId);
      if (item) price += item.price * qty;
    }
  }
  return price;
}

export const useCartActions = () => {
  const config = useUIStore((s) => s.config);
  const setSection = useUIStore((s) => s.setSection);
  const clearOrders = useOrdersStore((s) => s.clear);
  const revalidatePrices = useOrdersStore((s) => s.revalidatePrices);

  const menuLastUpdatedIso = useItemsStore((s) => s.menuLastUpdatedIso);
  const menuRestaurantId = useItemsStore((s) => s.menuRestaurantId);
  const menuMode = useItemsStore((s) => s.menuMode);
  const setMenuStore = useItemsStore((s) => s.setMenu);
  const setMenuMeta = useItemsStore((s) => s.setMenuMeta);
  const setMenuUpdatedWarning = useItemsStore((s) => s.setMenuUpdatedWarning);

  const sendOrderToWhatsApp = async (orders: Order[], total: number) => {
    if (menuRestaurantId && menuMode) {
      const fresh = await fetchMenuDtoFresh(menuRestaurantId, menuMode);
      if (fresh) {
        const categories = mapDTOToDomain(fresh, menuMode);
        const allProducts = categories.flatMap(c => c.products);

        const priceChanged = orders.some(
          order => computeOrderPrice(order, allProducts) !== order.price
        );

        if (priceChanged) {
          setMenuStore({ categories });
          revalidatePrices(categories);
          setMenuMeta(menuRestaurantId, menuMode, fresh.metadata.last_updated_iso);
          setMenuUpdatedWarning(true);
          setSection("cart");
          return;
        }

        // Sin cambio de precio: actualizar menú silenciosamente si la versión cambió
        if (fresh.metadata.last_updated_iso !== menuLastUpdatedIso) {
          setMenuStore({ categories });
          setMenuMeta(menuRestaurantId, menuMode, fresh.metadata.last_updated_iso);
        }
      }
    }

    setMenuUpdatedWarning(false);

    const phone = config.phoneNumber?.replace(/\D/g, "");

    const orderId = crypto.randomUUID().substring(0, 8).toUpperCase();

    const message = `
 *Pedido Web Menugraphic*

*N° Pedido:* #${orderId}

*DETALLE DEL PEDIDO*

${orders
  .map((o) => {
    const base = `• *${o.title} x${o.quantity}*`;

    const combos =
      o.comboSelection?.length
        ? "\n" +
          o.comboSelection
            .map((i: any) => `   * ${i.quantity}x ${i.title}`)
            .join("\n")
        : "";

    const options =
      o.resolvedOptions?.length
        ? "\n" +
          o.resolvedOptions
            .map((g) =>
              `   *${g.groupName}:*\n` +
              g.items
                .map(
                  (item) =>
                    `   - ${item.qty > 1 ? `${item.qty}x ` : ""}${item.name}`
                )
                .join("\n")
            )
            .join("\n")
        : "";

    return `${base}${combos}${options}`;
  })
  .join("\n\n")}

━━━━━━━━━━━━━━

 OBSERVACIONES:



━━━━━━━━━━━━━━

 Revisá bien tu pedido antes de enviarlo. Una vez enviado, el pedido y el total será confirmado por el local.
Muchas gracias!

`.trim();

    window.open(
      `https://wa.me/${phone}?text=${encodeURIComponent(message)}`,
      "_blank"
    );

    clearOrders();
    setSection("home");
  };

  return { sendOrderToWhatsApp };
};
